import React, { useRef } from 'react';
import { Calendar, Check, ExternalLink, Star, Trash2 } from 'lucide-react';
import {
  MenuPositionType,
  TaskDeleteHandler,
  TaskItem,
  TaskUpdateHandler,
} from './types/dashboardTypes';
import { Dropdown, DropdownContent, DropdownItem } from '../ui';
import { logger } from '../../utils/logger';
import { useViewport } from '../../contexts/ViewportContext';
import { MobileFullScreenModal } from '../ui/MobileFullScreenModal';

interface DashboardTaskMenuProps {
  task: TaskItem;
  position: MenuPositionType;
  onClose: () => void;
  onDelete: TaskDeleteHandler;
  onUpdateTask: TaskUpdateHandler;
  onNavigateToTask: (task: TaskItem) => void;
  onOpenDatePicker: (event: React.MouseEvent) => void;
}

const DashboardTaskMenu: React.FC<DashboardTaskMenuProps> = ({
  task,
  position,
  onClose,
  onDelete,
  onUpdateTask,
  onNavigateToTask,
  onOpenDatePicker,
}) => {
  const { isMobile } = useViewport();
  const anchorRef = useRef<HTMLDivElement>(null);

  // Empty span for the trigger since the real trigger is handled in the parent component
  const emptyTrigger = <span style={{ display: 'none' }} />;

  // Handle Go to Kanban action
  const handleNavigateToTask = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    logger.component('Go to Kanban button clicked', { taskId: task.id });
    onNavigateToTask(task);
    onClose();
  };

  // Handle Change Deadline action
  const handleOpenDatePicker = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    logger.component('Change deadline button clicked', {
      taskId: task.id,
      isMobile,
      targetExists: e.currentTarget ? true : false,
    });

    // For mobile, we pass a fullscreen flag to the parent
    if (isMobile) {
      // No need for the event on mobile as we'll use fullscreen mode
      const mobileEvent = {
        ...e,
        // Add a flag to indicate this is a mobile event
        isMobileEvent: true,
      };

      // Tell the parent to open the fullscreen date picker
      onOpenDatePicker(mobileEvent);

      // Close the menu after triggering the date picker
      onClose();
    } else {
      // For desktop, we need the position info from the event
      onOpenDatePicker(e);
      onClose();
    }
  };

  // Handle Mark as Done action
  const handleMarkAsDone = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    logger.component('Mark as done button clicked', { taskId: task.id });
    onClose();
    onUpdateTask(task.id, { status: 'done' });
  };

  // Handle Highlight/Unhighlight action
  const handleToggleHighlight = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    // Handle both snake_case and camelCase versions based on data source
    const isHighlighted =
      task.isHighlighted !== undefined ? task.isHighlighted : task.is_highlighted;
    const newHighlightStatus = !isHighlighted;

    logger.component(`${newHighlightStatus ? 'Highlight' : 'Unhighlight'} task button clicked`, {
      taskId: task.id,
      isCurrentlyHighlighted: !!isHighlighted,
    });

    onClose();
    // Use the field name expected by the mutation (camelCase)
    onUpdateTask(task.id, { isHighlighted: newHighlightStatus });
  };

  // Handle Delete action
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    // Extract topicId from the task's topics field
    const topicId = task.topics?.id;

    logger.component('Delete button clicked', { taskId: task.id, topicId });
    onClose();
    onDelete(task.id, topicId);
  };

  // The key is using a consistent positioning approach
  // For fixed-position dropdowns, we'll create an element at the original position
  // and let the dropdown component handle positioning relative to that
  const anchorStyle: React.CSSProperties = {
    position: 'fixed',
    top: `${position.top - 30}px`, // Offset upward so menu appears below
    left: `${position.left}px`,
    width: '1px',
    height: '1px',
    pointerEvents: 'none',
    visibility: 'hidden',
  };

  if (isMobile) {
    return (
      <MobileFullScreenModal
        isOpen={true}
        onClose={onClose}
        title="Task Options"
        footerContent={
          <button
            onClick={onClose}
            className="w-full py-2.5 px-4 bg-blue-500 text-white font-medium rounded-md hover:bg-blue-600 transition-colors text-lg"
          >
            Done
          </button>
        }
      >
        {/* Main Actions */}
        <div>
          <div className="px-4 py-3 bg-gray-50 flex justify-between items-center">
            <h3 className="font-medium text-gray-700">Actions</h3>
          </div>

          {/* Go to Kanban */}
          <button
            onClick={handleNavigateToTask}
            className="flex items-center w-full px-4 py-3 text-left hover:bg-gray-100 cursor-pointer"
          >
            <div className="flex-1 flex items-center gap-2">
              <ExternalLink size={16} className="text-blue-500" />
              <span>Go to Kanban</span>
            </div>
          </button>

          {/* Change Deadline */}
          <button
            onClick={handleOpenDatePicker}
            className="flex items-center w-full px-4 py-3 text-left hover:bg-gray-100 cursor-pointer"
          >
            <div className="flex-1 flex items-center gap-2">
              <Calendar size={16} className="text-blue-500" />
              <span>Change Deadline</span>
            </div>
          </button>

          {/* Mark as Done */}
          <button
            onClick={handleMarkAsDone}
            className="flex items-center w-full px-4 py-3 text-left hover:bg-gray-100 cursor-pointer"
          >
            <div className="flex-1 flex items-center gap-2">
              <Check size={16} className="text-green-600" />
              <span>Mark as Done</span>
            </div>
          </button>

          {/* Highlight Task */}
          <button
            onClick={handleToggleHighlight}
            className="flex items-center w-full px-4 py-3 text-left hover:bg-gray-100 cursor-pointer"
          >
            <div className="flex-1 flex items-center gap-2">
              <Star
                size={16}
                className={
                  task.isHighlighted !== undefined
                    ? task.isHighlighted
                      ? 'text-yellow-500 fill-yellow-500'
                      : 'text-yellow-500'
                    : task.is_highlighted
                      ? 'text-yellow-500 fill-yellow-500'
                      : 'text-yellow-500'
                }
              />
              <span>
                {task.isHighlighted !== undefined
                  ? task.isHighlighted
                    ? 'Remove Highlight'
                    : 'Highlight Task'
                  : task.is_highlighted
                    ? 'Remove Highlight'
                    : 'Highlight Task'}
              </span>
            </div>
          </button>

          {/* Delete Task */}
          <button
            onClick={handleDelete}
            className="flex items-center w-full px-4 py-3 text-left hover:bg-gray-100 cursor-pointer"
          >
            <div className="flex-1 flex items-center gap-2">
              <Trash2 size={16} className="text-red-500" />
              <span className="text-red-500">Delete Task</span>
            </div>
          </button>
        </div>
      </MobileFullScreenModal>
    );
  }

  // Desktop implementation unchanged
  return (
    <>
      {/* Invisible anchor element at the calculated position */}
      <div ref={anchorRef} style={anchorStyle} />

      <Dropdown
        trigger={emptyTrigger}
        open={true}
        onOpenChange={isOpen => {
          if (!isOpen) onClose();
        }}
        position="bottom-left"
        width={isMobile ? 250 : 200} // Slightly wider on mobile for better touch targets
        contentClassName={`shadow-lg z-[9999] border-0 ${isMobile ? 'py-1' : ''}`}
        isMobile={isMobile}
        anchorElement={anchorRef.current}
        showBackdrop={isMobile} // Show backdrop overlay on mobile
        mobilePosition={0.2} // Position 20% from the top of the screen
      >
        <DropdownContent>
          {/* Navigate to Kanban board */}
          <DropdownItem
            leftIcon={<ExternalLink size={isMobile ? 18 : 14} />}
            onClick={handleNavigateToTask}
          >
            Go to Kanban
          </DropdownItem>

          {/* Change deadline option */}
          <DropdownItem
            leftIcon={<Calendar size={isMobile ? 18 : 14} />}
            onClick={handleOpenDatePicker}
          >
            Change Deadline
          </DropdownItem>

          {/* Mark as done option */}
          {isMobile && (
            <DropdownItem
              leftIcon={<Check size={isMobile ? 18 : 14} className="text-green-600" />}
              onClick={handleMarkAsDone}
            >
              Mark as Done
            </DropdownItem>
          )}

          {/* Highlight/Unhighlight option */}
          <DropdownItem
            leftIcon={
              <Star
                size={isMobile ? 18 : 14}
                className={
                  task.isHighlighted !== undefined
                    ? task.isHighlighted
                      ? 'text-yellow-500 fill-yellow-500'
                      : 'text-yellow-500'
                    : task.is_highlighted
                      ? 'text-yellow-500 fill-yellow-500'
                      : 'text-yellow-500'
                }
              />
            }
            onClick={handleToggleHighlight}
          >
            {task.isHighlighted !== undefined
              ? task.isHighlighted
                ? 'Remove Highlight'
                : 'Highlight Task'
              : task.is_highlighted
                ? 'Remove Highlight'
                : 'Highlight Task'}
          </DropdownItem>

          {/* Delete task option */}
          <DropdownItem
            leftIcon={<Trash2 size={isMobile ? 18 : 14} className="text-red-600" />}
            onClick={handleDelete}
            className="text-red-600 hover:bg-red-50"
          >
            Delete Task
          </DropdownItem>
        </DropdownContent>
      </Dropdown>
    </>
  );
};

export default DashboardTaskMenu;
